import React, { use, useEffect, useState } from "react";
import { Table, Form, Button, Row, Col, Card } from "react-bootstrap";
import {
	toggleRiderStatus,
	updateRider,
} from "../../../../services/riderService";
import {
	BrandTeam,
	initialBrandTeam,
	initialRider,
	initialSeason,
	Rider,
	Season,
	Series,
	Sport,
	TeamHistory,
} from "../../../../types/model";
import { getBrandTeams } from "../../../../services/brandTeamService";
import { getCurrentSeason } from "../../../../services/seasonService";

interface RiderListProps {
	riders: Rider[];
	setRider: (rider: Rider | null) => void;
	toggleRider: (rider: Rider) => void;
	sports: Sport[];
	series: Series[];
	fetchRiders: () => void;
}

const RiderList = ({
	riders,
	setRider,
	toggleRider,
	sports,
	series,
	fetchRiders,
}: RiderListProps) => {
	const [sportFilter, setSportFilter] = useState("");
	const [seriesFilter, setSeriesFilter] = useState("");
	const [sortField, setSortField] = useState("name");
	const [sortOrder, setSortOrder] = useState("asc");
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedRider, setSelectedRider] = useState<Rider | null>(null);
	const [currentSeason, setCurrentSeason] = useState<Season>(initialSeason);
	const [brandTeams, setBrandTeams] = useState<BrandTeam[]>([]);

	const handleSelectRider = (rider: Rider) => {
		// Set the selected rider
		setSelectedRider(rider);
		setRider(rider);
	};

	useEffect(() => {
		// Fetch current season and brand teams when component mounts
		fetchCurrentSeason();
		fetchBrandTeams();
	}, []);

	const handleUpdateRider = async () => {
		// Update
		try {
			if (selectedRider?._id) {
				const res = await updateRider(selectedRider._id, selectedRider);

				fetchRiders();
				setSelectedRider(null);
				setRider(null);
			} else {
				alert("No rider selected");
			}
		} catch (error) {
			console.error("Error updating rider:", error);
			alert("Failed to update rider");
		}
	};

	const handleSortChange = (field: string) => {
		if (sortField === field) {
			// Toggle sort order if clicking the same field
			setSortOrder(sortOrder === "asc" ? "desc" : "asc");
		} else {
			// Set new field and default to ascending
			setSortField(field);
			setSortOrder("asc");
		}
	};

	const fetchCurrentSeason = async () => {
		try {
			const res = await getCurrentSeason();
			if (res) {
				setCurrentSeason(res.data);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const fetchBrandTeams = async () => {
		try {
			const res = await getBrandTeams();
			if (res) {
				//Sort in alphabetical order
				res.data.sort((a: BrandTeam, b: BrandTeam) =>
					a.name.localeCompare(b.name),
				);
				//Set brand teams
				setBrandTeams(res.data);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const filteredRiders = riders.filter((rider) => {
		// Apply filters
		if (sportFilter && rider?.sports?.indexOf(sportFilter) === -1) {
			return false;
		}
		if (seriesFilter && rider?.series?.indexOf(seriesFilter) === -1) {
			return false;
		}
		if (
			searchQuery &&
			!rider.first_name.toLowerCase().includes(searchQuery.toLowerCase())
		) {
			return false;
		}
		return true;
	});

	const handleTeamHistoryChange = (e: any) => {
		const { value } = e.target;
		const selectedTeam = brandTeams.find((team) => team._id === value);
		if (
			selectedTeam &&
			selectedRider &&
			selectedTeam._id &&
			currentSeason?._id
		) {
			setSelectedRider({
				...selectedRider,
				teamHistory: [
					...(selectedRider.teamHistory || []).filter(
						(team: TeamHistory) => team.season !== currentSeason._id,
					),
					{
						season: currentSeason._id,
						team: selectedTeam._id,
					},
				],
			});
		}
	};

	const sortedRiders = [...filteredRiders].sort((a, b) => {
		// Sort riders based on sortField
		switch (sortField) {
			case "name":
				const nameA = a.first_name.toLowerCase();
				const nameB = b.first_name.toLowerCase();
				return sortOrder === "asc"
					? nameA.localeCompare(nameB)
					: nameB.localeCompare(nameA);
			case "status":
				const statusA = (a.status || "active").toLowerCase();
				const statusB = (b.status || "active").toLowerCase();
				return sortOrder === "asc"
					? statusA.localeCompare(statusB)
					: statusB.localeCompare(statusA);
			case "gender":
				const genderA = (a.gender || "").toLowerCase();
				const genderB = (b.gender || "").toLowerCase();
				return sortOrder === "asc"
					? genderA.localeCompare(genderB)
					: genderB.localeCompare(genderA);
			case "rating":
				// Find ratings for the selected sport
				const ratingA =
					a.ratings?.find((r) => r.sport === sportFilter)?.rating || 0;
				const ratingB =
					b.ratings?.find((r) => r.sport === sportFilter)?.rating || 0;
				return sortOrder === "asc" ? ratingA - ratingB : ratingB - ratingA;
			default:
				return 0;
		}
	});

	// Helper function to render sort indicator
	const renderSortIndicator = (field: string) => {
		if (sortField === field) {
			return sortOrder === "asc" ? " ▲" : " ▼";
		}
		return null;
	};

	return (
		<div>
			{/* Filters and search */}
			<Card className="my-3 p-4 border-0 shadow">
				<Card.Title className="m-3 ms-4 fw-bold">Filter Riders</Card.Title>
				<Card.Body style={{ maxHeight: "30rem", overflow: "auto" }}>
					<Form>
						<Row className="mb-3">
							<Col sm={3}>
								<Form.Control
									as="select"
									value={sportFilter}
									onChange={(e) => setSportFilter(e.target.value)}
								>
									<option value="">All Sports</option>
									{sports.map((sport) => (
										<option key={sport._id} value={sport._id}>
											{sport.name}
										</option>
									))}
								</Form.Control>
							</Col>
							<Col sm={3}>
								<Form.Control
									as="select"
									value={seriesFilter}
									onChange={(e) => setSeriesFilter(e.target.value)}
								>
									<option value="">All Series</option>
									{series.map((series) => (
										<option key={series._id} value={series._id}>
											{series.name}
										</option>
									))}
								</Form.Control>
							</Col>
							<Col sm={3}>
								<Form.Control
									as="select"
									value={`${sortField}-${sortOrder}`}
									onChange={(e) => {
										const [field, order] = e.target.value.split("-");
										setSortField(field);
										setSortOrder(order);
									}}
								>
									<option value="name-asc">Name (A-Z)</option>
									<option value="name-desc">Name (Z-A)</option>
									<option value="rating-asc">Rating (Low-High)</option>
									<option value="rating-desc">Rating (High-Low)</option>
									<option value="status-asc">Status (A-Z)</option>
									<option value="status-desc">Status (Z-A)</option>
									<option value="gender-asc">Gender (A-Z)</option>
									<option value="gender-desc">Gender (Z-A)</option>
								</Form.Control>
							</Col>
							<Col sm={3}>
								<Form.Control
									type="text"
									placeholder="Search rider..."
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
								/>
							</Col>
						</Row>
					</Form>

					{/* Rider list table */}
					<Table striped bordered hover style={{ fontSize: "0.8rem" }}>
						<thead>
							<tr>
								<th
									onClick={() => handleSortChange("name")}
									style={{ cursor: "pointer" }}
								>
									Name {renderSortIndicator("name")}
								</th>
								<th>Country</th>
								<th
									onClick={() => handleSortChange("status")}
									style={{ cursor: "pointer" }}
								>
									Status {renderSortIndicator("status")}
								</th>
								<th
									onClick={() => handleSortChange("rating")}
									style={{ cursor: "pointer" }}
								>
									Rating {renderSortIndicator("rating")}
								</th>
								<th
									onClick={() => handleSortChange("gender")}
									style={{ cursor: "pointer" }}
								>
									Gender {renderSortIndicator("gender")}
								</th>
								<th>Current Team</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{sortedRiders.map((rider: Rider) => {
								// Find the rating for the selected sport
								const ratingObj = rider.ratings?.find(
									(r) => r.sport === sportFilter,
								);
								const ratingValue = ratingObj?.rating || "-";

								return (
									<tr key={rider._id} className="p-0 m-0">
										<td className="p-0 ps-2">
											{`${rider.first_name} ${rider.last_name}`}
										</td>
										<td className="p-0 ps-2">
											{rider._id === selectedRider?._id ? (
												<Form.Control
													type="text"
													size="sm"
													value={selectedRider?.country || ""}
													onChange={(e) =>
														selectedRider &&
														setSelectedRider({
															...selectedRider,
															country: e.target.value,
														})
													}
												/>
											) : (
												rider.country || "-"
											)}
										</td>
										<td className="p-0 ps-2">
											<span
												style={{
													color:
														rider.status?.toLowerCase() === "active"
															? "green"
															: "red",
												}}
											>
												{rider.status || "none"}
											</span>
										</td>
										<td className="p-0 ps-2">
											{rider._id === selectedRider?._id && sportFilter ? (
												<Form.Control
													type="number"
													size="sm"
													value={
														selectedRider?.ratings?.find(
															(r) => r.sport === sportFilter,
														)?.rating || ""
													}
													onChange={(e) => {
														if (selectedRider) {
															const newRatings = [
																...(selectedRider.ratings || []),
															];
															const ratingIndex = newRatings.findIndex(
																(r) => r.sport === sportFilter,
															);

															if (ratingIndex >= 0) {
																newRatings[ratingIndex] = {
																	...newRatings[ratingIndex],
																	rating: parseFloat(e.target.value),
																};
															} else {
																newRatings.push({
																	sport: sportFilter,
																	rating: parseFloat(e.target.value),
																});
															}

															setSelectedRider({
																...selectedRider,
																ratings: newRatings,
															});
														}
													}}
												/>
											) : (
												ratingValue
											)}
										</td>
										<td className="p-0 ps-2">
											{rider._id === selectedRider?._id ? (
												<Form.Control
													as="select"
													size="sm"
													value={selectedRider?.gender || ""}
													onChange={(e) =>
														selectedRider &&
														setSelectedRider({
															...selectedRider,
															gender: e.target.value,
														})
													}
												>
													<option value="m">m</option>
													<option value="f">f</option>
													<option value="tbd">tbd</option>
												</Form.Control>
											) : (
												rider.gender || "-"
											)}
										</td>
										<td className="p-0 ps-2">
											{rider._id === selectedRider?._id ? (
												<Form.Control
													as="select"
													size="sm"
													value={
														selectedRider?.teamHistory?.find(
															(team) => team.season === currentSeason?._id,
														)?.team || ""
													}
													onChange={handleTeamHistoryChange}
												>
													<option value="">Select Team</option>
													{brandTeams.map((team) => (
														<option key={team._id} value={team._id}>
															{team.name}
														</option>
													))}
												</Form.Control>
											) : (
												brandTeams.find(
													(team) =>
														team._id ===
														rider?.teamHistory?.find(
															(hist) => hist.season === currentSeason?._id,
														)?.team,
												)?.name || "-"
											)}
										</td>
										<td className="p-0 ps-2">
											<Button
												size="sm"
												variant="outline-primary"
												onClick={() => toggleRider(rider)}
												className="me-2"
											>
												TOGGLE STATUS
											</Button>
											<Button
												size="sm"
												variant="outline-primary"
												onClick={() => handleSelectRider(rider)}
												className="me-2"
											>
												SELECT
											</Button>
											{rider._id === selectedRider?._id && (
												<Button
													size="sm"
													variant="success"
													className="text-white"
													onClick={handleUpdateRider}
												>
													SAVE
												</Button>
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</Table>
				</Card.Body>
			</Card>
		</div>
	);
};

export default RiderList;
